import { ChangeDetectionStrategy, Component, OnInit, inject, input, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { ButtonDirective } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { FloatLabel } from 'primeng/floatlabel';
import { InputNumber } from 'primeng/inputnumber';
import { Select } from 'primeng/select';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Utils } from '../../../../../services/utils.service';
import { Api } from '../../../../../services/api.service';
import { IClient } from '../../../../../../../../../../common/common-interfaces/client';
import { IBookingForm } from '../../../../../../../../../../common/common-interfaces/booking-form';

interface AutoPromoCodePlaceholderData {
    uses: number;
    validDays: number;
    type: 'percent' | 'amount';
    percent: number;
    amount: number;
}

interface BookingLinkPlaceholderData {
    promoCodeEnabled: boolean;
    promoCodeUses: number;
    promoCodeValidHours: number;
    promoCodeValidMinutes: number;
    promoCodeType: 'percent' | 'amount';
    promoCodePercent: number;
    promoCodeAmount: number;
    bookingFormId: IBookingForm['id'] | null;
    autoCheckIn: boolean;
}

@Component({
    selector: 'placeholders-drawer',
    templateUrl: './placeholders-drawer.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, TranslateDirective, TranslatePipe, ButtonDirective, Drawer, FloatLabel, InputNumber, Select, ToggleSwitch]
})
export class PlaceholdersDrawerComponent implements OnInit {
    public utility = inject(Utils);
    private api = inject(Api);

    visible = model<boolean>(false);
    selectedClient = input<IClient | undefined>(undefined);
    // Hides the static placeholders (the Maillune editor offers those as merge tags).
    dynamicOnly = input<boolean>(false);

    readonly placeholderItems = [
        { label: 'BOOKING_NUMBER', value: '{BOOKING_NUMBER}' },
        { label: 'CUSTOMER_FIRST_NAME', value: '{FIRST_NAME}' },
        { label: 'CUSTOMER_LAST_NAME', value: '{LAST_NAME}' },
        { label: 'BIRTHDAY_PERSON_NAME', value: '{BIRTHDAY_PERSON_NAME}' }
    ];

    autoPromoCodePlaceholderData = signal<AutoPromoCodePlaceholderData>({
        uses: 1,
        validDays: 90,
        type: 'percent',
        percent: 10,
        amount: 0
    });

    bookingLinkPlaceholderData = signal<BookingLinkPlaceholderData>({
        promoCodeEnabled: true,
        promoCodeUses: 1,
        promoCodeValidHours: 24,
        promoCodeValidMinutes: 0,
        promoCodeType: 'percent',
        promoCodePercent: 10,
        promoCodeAmount: 0,
        bookingFormId: null,
        autoCheckIn: false
    });

    autoPromoCode = signal<string>('');
    bookingLink = signal<string>('');

    availableBookingForms = signal<IBookingForm[]>([]);

    async ngOnInit(): Promise<void> {
        this.buildPromoCode();
        this.buildBookingLink();
        await this.fetchBookingForms();
    }

    close(): void {
        this.visible.set(false);
    }

    updateAutoPromoCodeData(patch: Partial<AutoPromoCodePlaceholderData>): void {
        this.autoPromoCodePlaceholderData.update((d) => ({ ...d, ...patch }));
        this.buildPromoCode();
    }

    buildPromoCode(): void {
        const current = this.autoPromoCodePlaceholderData();
        const data: any = {
            uses: current.uses,
            validDays: current.validDays
        };

        if (current.type === 'percent') {
            data.percent = current.percent;
        } else {
            data.amount = current.amount;
        }

        this.autoPromoCode.set(`({PROMO_CODE:${JSON.stringify(data)}})`);
    }

    // ── Booking link ─────────────────────────────────────────────────────────

    updateBookingLinkData(patch: Partial<BookingLinkPlaceholderData>): void {
        this.bookingLinkPlaceholderData.update((d) => ({ ...d, ...patch }));
        this.buildBookingLink();
    }

    buildBookingLink(): void {
        const current = this.bookingLinkPlaceholderData();
        let next = current;
        const data: any = {};

        if (next.bookingFormId) {
            data.bookingFormId = next.bookingFormId;
        }

        if (next.autoCheckIn) {
            data.autoCheckIn = next.autoCheckIn;
        }

        if (next.promoCodeEnabled) {
            data.promoCodeUses = next.promoCodeUses;

            // Hours and minutes are mutually exclusive - setting one clears the other,
            // same as the original implementation.
            if (next.promoCodeValidHours) {
                data.promoCodeValidHours = next.promoCodeValidHours;
                if (next.promoCodeValidMinutes !== 0) {
                    next = { ...next, promoCodeValidMinutes: 0 };
                }
            }
            if (next.promoCodeValidMinutes) {
                data.promoCodeValidMinutes = next.promoCodeValidMinutes;
                if (next.promoCodeValidHours !== 0) {
                    next = { ...next, promoCodeValidHours: 0 };
                }
            }

            data.promoCodeType = next.promoCodeType;

            if (next.promoCodeType === 'percent') {
                data.promoCodePercent = next.promoCodePercent;
            } else {
                data.promoCodeAmount = next.promoCodeAmount;
            }
        }

        if (next !== current) {
            this.bookingLinkPlaceholderData.set(next);
        }
        this.bookingLink.set(`({BOOKING_LINK:${JSON.stringify(data)}})`);
    }

    async fetchBookingForms(): Promise<void> {
        const client = this.selectedClient();
        if (!client) {
            return;
        }
        const forms = (await this.api.client(client.id).get<IBookingForm[]>('/manage/booking-forms')).data ?? [];
        this.availableBookingForms.set(forms);
        if (forms.length) {
            this.updateBookingLinkData({ bookingFormId: forms[0].id });
        }
    }

    copyAndClose(value: string): void {
        this.utility.copyToClipboard(value);
        this.close();
    }
}
